/* eslint-disable local/no-hardcoded-colors */
'use client';

import { useEffect } from 'react';

import { reportError } from '@/shared/utils/reportError';

/**
 * Root error boundary. Catches errors thrown in the root layout/template that
 * `error.tsx` files cannot catch. It REPLACES the root layout, so it must render
 * its own <html> and <body>. Because the app layout (fonts, providers,
 * design-system context) is bypassed here, this fallback is intentionally
 * self-contained and uses inline styles. Hex fallbacks are provided alongside
 * design-system CSS variables in case the global stylesheet failed to load,
 * which is why hardcoded colors are explicitly allowed in this file.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportError(error, { boundary: 'global-error' });
  }, [error]);

  return (
    <html lang="pt-BR">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
          backgroundColor: 'var(--bg-secondary, #f9fafb)',
          fontFamily:
            'var(--font-inter), Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          color: 'var(--text-primary, #171717)',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 560,
            borderRadius: 20,
            border: '1px solid var(--border-default, #e5e7eb)',
            backgroundColor: 'var(--text-inverse, #ffffff)',
            padding: 32,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              margin: '0 auto 20px',
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: 'var(--error-light, #fecaca)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 8v5"
                stroke="var(--error-dark, #dc2626)"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="12" cy="16.5" r="1.25" fill="var(--error-dark, #dc2626)" />
              <circle cx="12" cy="12" r="9" stroke="var(--error-dark, #dc2626)" strokeWidth="2" />
            </svg>
          </div>

          <h1 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 8px' }}>Algo deu errado</h1>
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.5,
              color: 'var(--text-secondary, #525252)',
              margin: '0 0 24px',
            }}
          >
            Ocorreu um erro inesperado ao carregar a aplicação. Tente novamente. Se o problema
            persistir, atualize a página.
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => reset()}
              style={{
                height: 40,
                padding: '0 16px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                backgroundColor: 'var(--primary-color, #2a1b63)',
                color: 'var(--text-inverse, #ffffff)',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Tentar novamente
            </button>
            <button
              type="button"
              onClick={() => window.location.assign('/')}
              style={{
                height: 40,
                padding: '0 16px',
                borderRadius: 8,
                border: '1px solid var(--border-default, #e5e7eb)',
                cursor: 'pointer',
                backgroundColor: 'var(--text-inverse, #ffffff)',
                color: 'var(--text-primary, #171717)',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Ir para o início
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
